import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { EMPTY, forkJoin } from 'rxjs';
import { tap } from 'rxjs/operators';
import { patch, removeItem } from '@ngxs/store/operators';
import { BaseTestItemDto } from 'src/app/models/dto/Base-Test-item-dto';
import { TestSpecificationStateModel } from './testSpecifications.state.model';
import { TestSpecificationApiService } from 'src/app/services/api/test-specification-api.service';
import { TestSpecificationsActions } from './testSpecifications.actions';

@State<TestSpecificationStateModel>({
  name: 'testSpecifications',
  defaults: {
    testSpecifications: [],
    orgGroupTestSpecifications: [],
    functionalGroupTestSpecifications: [],
    myTestSpecifications: []
  },
})
@Injectable()
export class TestSpecificationsState {
  //pageNumber = 1;
  //pageSize = 10;
  constructor(private testSpecificationApiService: TestSpecificationApiService) {}

  @Action(TestSpecificationsActions.SetAllTestSpecifications)
  SetAllTestSpecifications(ctx: StateContext<TestSpecificationStateModel>, action: TestSpecificationsActions.SetAllTestSpecifications) {
    const state = ctx.getState();
    //don't update state if we have already downloaded items
    if (state.testSpecifications !== undefined && state.testSpecifications.length > 0)
      return EMPTY;

    return this.testSpecificationApiService.getAll(action.company, '', '').pipe(
      tap(testSpecifications =>{
        ctx.patchState({ testSpecifications: testSpecifications });
      })
    );
  }

  @Action(TestSpecificationsActions.SetOrgGroupTestSpecifications)
  SetOrgGroupTestSpecifications(ctx: StateContext<TestSpecificationStateModel>, action: TestSpecificationsActions.SetOrgGroupTestSpecifications) {
    const state = ctx.getState();
    //don't update state if we have already downloaded items
    if (state.orgGroupTestSpecifications !== undefined && state.orgGroupTestSpecifications.length > 0)
      return EMPTY;

    return this.testSpecificationApiService.getAll('', '', action.orgGroupId ).pipe(
      tap(testSpecifications =>{
        ctx.patchState({ orgGroupTestSpecifications: testSpecifications });
      })
    );
  }

  @Action(TestSpecificationsActions.SetMyTestSpecifications)
  SetMyTestSpecifications(ctx: StateContext<TestSpecificationStateModel>, action: TestSpecificationsActions.SetMyTestSpecifications) {
    const state = ctx.getState();
    //don't update state if we have already downloaded items
    if (state.myTestSpecifications !== undefined && state.myTestSpecifications.length > 0)
      return EMPTY;

    return this.testSpecificationApiService.getAll('', action.userId, '' ).pipe(
      tap(testSpecifications =>{
        ctx.patchState({ myTestSpecifications: testSpecifications });
      })
    );
  }

  @Action(TestSpecificationsActions.SetFunctionalGroupTestSpecifications)
  SetFunctionalGroupTestSpecifications(ctx: StateContext<TestSpecificationStateModel>, action: TestSpecificationsActions.SetFunctionalGroupTestSpecifications) {
    const state = ctx.getState();
    //don't update state if we have already downloaded items
    if (state.functionalGroupTestSpecifications !== undefined && state.functionalGroupTestSpecifications.length > 0)
      return EMPTY;

    // The group id goes in the third (OrgGroup) slot. It used to land in regBy, which the
    // old API matched against the creator (so: nothing) and the Go path turned into scope
    // MINE (so: the caller's own items). Results are merged and de-duplicated — each
    // response used to overwrite the previous one. On the Go path every call resolves to
    // scope MY_GROUP, i.e. items owned by ANY of the caller's groups, so the merge is that set.
    const observables = action.functionalGroupIds.map(fg => this.testSpecificationApiService.getAll('', '', fg));
    if (observables.length === 0) return EMPTY;
    return forkJoin(observables).pipe(
      tap(results => {
        const byUid = new Map<string, BaseTestItemDto>();
        for (const list of results) {
          for (const item of list ?? []) {
            if (item?.uid) byUid.set(item.uid, item);
          }
        }
        ctx.patchState({ functionalGroupTestSpecifications: [...byUid.values()] });
      })
    );
  }

  @Action(TestSpecificationsActions.DeleteTestSpecification)
  DeleteTestSpecification(ctx: StateContext<TestSpecificationStateModel>, action: TestSpecificationsActions.DeleteTestSpecification){
    return this.testSpecificationApiService.delete(action.testSpecificationUid, action.regBy).pipe(
      tap(result =>{
        ctx.setState(
          patch<TestSpecificationStateModel>({
            orgGroupTestSpecifications: removeItem<BaseTestItemDto>(x => x.uid === action.testSpecificationUid),
            testSpecifications: removeItem<BaseTestItemDto>(x => x.uid === action.testSpecificationUid)
          })
        )
      })
    );
  }

  @Action(TestSpecificationsActions.UpdateItem)
  updateItem(ctx: StateContext<TestSpecificationStateModel>, action: TestSpecificationsActions.UpdateItem) {
    const state = ctx.getState();

    // Update in testSpecifications (All)
    const allIdx = state.testSpecifications.findIndex(item => item.uid === action.item.uid);
    if (allIdx >= 0) {
      const updated = [...state.testSpecifications];
      updated[allIdx] = action.item;
      ctx.patchState({ testSpecifications: updated });
    }

    // Update in orgGroupTestSpecifications
    const orgIdx = state.orgGroupTestSpecifications.findIndex(item => item.uid === action.item.uid);
    if (orgIdx >= 0) {
      const updated = [...state.orgGroupTestSpecifications];
      updated[orgIdx] = action.item;
      ctx.patchState({ orgGroupTestSpecifications: updated });
    }

    // Update in myTestSpecifications
    const myIdx = state.myTestSpecifications.findIndex(item => item.uid === action.item.uid);
    if (myIdx >= 0) {
      const updated = [...state.myTestSpecifications];
      updated[myIdx] = action.item;
      ctx.patchState({ myTestSpecifications: updated });
    }

    // Update in functionalGroupTestSpecifications
    const fgIdx = state.functionalGroupTestSpecifications.findIndex(item => item.uid === action.item.uid);
    if (fgIdx >= 0) {
      const updated = [...state.functionalGroupTestSpecifications];
      updated[fgIdx] = action.item;
      ctx.patchState({ functionalGroupTestSpecifications: updated });
    }
  }

  @Action(TestSpecificationsActions.ClearCache)
  clearCache(ctx: StateContext<TestSpecificationStateModel>) {
    ctx.patchState({
      testSpecifications: [],
      orgGroupTestSpecifications: [],
      functionalGroupTestSpecifications: [],
      myTestSpecifications: []
    });
  }

  @Selector()
  static getAllTestSpecifications(state: TestSpecificationStateModel) {
    return state.testSpecifications;
  }

  @Selector()
  static getOrgGroupTestSpecifications(state: TestSpecificationStateModel) {
    return state.orgGroupTestSpecifications;
  }

  @Selector()
  static getMyTestSpecifications(state: TestSpecificationStateModel) {
    return state.myTestSpecifications;
  }

  @Selector()
  static getFgTestSpecifications(state: TestSpecificationStateModel) {
    return state.functionalGroupTestSpecifications;
  }
}
